import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';


const Header = ({ userRole = 'publisher', isNotificationOpen = false, onNotificationToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationCount, setNotificationCount] = useState(3);
  const [realtimeEarnings, setRealtimeEarnings] = useState(2847.50);

  const navigationSections = [
    {
      id: 'publisher',
      label: 'Publisher Hub',
      icon: 'Phone',
      description: 'Call submission and campaign performance',
      items: [
        { path: '/publisher-dashboard', label: 'Dashboard' },
        { path: '/call-submission-interface', label: 'Submit Calls' }
      ]
    },
    {
      id: 'buyer',
      label: 'Buyer Center',
      icon: 'Users',
      description: 'Registration and bidding management',
      items: [
        { path: '/buyer-registration-profile-management', label: 'Profile Management' },
        { path: '/buyer-bid-management-interface', label: 'Bid Management' }
      ]
    },
    {
      id: 'auction',
      label: 'Auction Control',
      icon: 'Activity',
      description: 'Real-time bidding monitoring',
      items: [
        { path: '/real-time-bidding-engine-dashboard', label: 'Bidding Engine' }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: 'BarChart3',
      description: 'Revenue and performance insights',
      items: [
        { path: '/revenue-analytics-reporting-dashboard', label: 'Revenue Reports' }
      ]
    }
  ];

  useEffect(() => {
    const currentPath = location?.pathname;
    const currentSection = navigationSections?.find(section =>
      section?.items?.some(item => item?.path === currentPath)
    );
    if (currentSection) {
      setActiveSection(currentSection?.id);
    }
  }, [location?.pathname]);

  useEffect(() => {
    const interval = setInterval(() => {
      setRealtimeEarnings(prev => prev + Math.random() * 10);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleSectionClick = (section) => {
    if (section?.items?.length > 0) {
      navigate(section?.items?.[0]?.path);
    }
    setIsMobileMenuOpen(false);
  };

  const handleNotificationClick = () => {
    if (onNotificationToggle) {
      onNotificationToggle();
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 bg-card border-b border-border z-1000">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Logo */}
        <div className="flex items-center">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="Shield" size={20} color="white" />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-foreground">Insure Connect</h1>
              <p className="text-xs text-muted-foreground -mt-1">Marketplace</p>
            </div>
          </div>
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-1">
          {navigationSections?.map((section) => (
            <button
              key={section?.id}
              onClick={() => handleSectionClick(section)}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-smooth hover:bg-muted ${
                activeSection === section?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title={section?.description}
            >
              <Icon name={section?.icon} size={16} />
              <span>{section?.label}</span>
            </button>
          ))}
        </nav>

        {/* User Context & Actions */}
        <div className="flex items-center space-x-3">
          {/* Real-time Earnings */}
          <div className="hidden md:flex items-center space-x-2 px-3 py-1.5 bg-success/10 rounded-lg">
            <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
            <span className="text-sm font-mono text-success">
              ${realtimeEarnings?.toFixed(2)}
            </span>
          </div>

          {/* Notifications */}
          <button
            onClick={handleNotificationClick}
            className="relative p-2 text-muted-foreground hover:text-foreground transition-smooth hover:bg-muted rounded-lg"
          >
            <Icon name="Bell" size={20} />
            {notificationCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-error text-error-foreground text-xs rounded-full flex items-center justify-center">
                {notificationCount}
              </span>
            )}
          </button>

          {/* User Menu */}
          <div className="flex items-center space-x-2 px-3 py-1.5 bg-muted rounded-lg">
            <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
              <Icon name="User" size={14} color="white" />
            </div>
            <span className="hidden sm:block text-sm font-medium text-foreground capitalize">
              {userRole}
            </span>
            <Icon name="ChevronDown" size={16} className="text-muted-foreground" />
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-muted-foreground hover:text-foreground transition-smooth hover:bg-muted rounded-lg"
          >
            <Icon name={isMobileMenuOpen ? "X" : "Menu"} size={20} />
          </button>
        </div>
      </div>
      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border">
          <nav className="px-4 py-3 space-y-1">
            {navigationSections?.map((section) => (
              <button
                key={section?.id}
                onClick={() => handleSectionClick(section)}
                className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-smooth ${
                  activeSection === section?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                <Icon name={section?.icon} size={18} />
                <div className="text-left">
                  <div>{section?.label}</div>
                  <div className="text-xs opacity-75">{section?.description}</div>
                </div>
              </button>
            ))}
          </nav>
          
          {/* Mobile Earnings Display */}
          <div className="px-4 pb-3 border-t border-border">
            <div className="flex items-center justify-between py-2">
              <span className="text-sm text-muted-foreground">Real-time Earnings</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full pulse-live"></div>
                <span className="text-sm font-mono text-success">
                  ${realtimeEarnings?.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;